import { useEffect, useState } from "react";
import { api, fileUrl, formatApiError } from "../lib/api";
import { PageHeader, Card, Modal, Field, inputClass, EmptyState, PrimaryButton } from "../components/common";
import { SignaturePad } from "../components/SignaturePad";
import { TimemarkCamera } from "../components/TimemarkCamera";
import { Lightbox } from "../components/Lightbox";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Notebook, Plus, Trash, Cloud, Warning, TrendUp, Note, Wrench, Printer, PenNib, MapPin } from "@phosphor-icons/react";

const CATS = [
  { value: "general", label: "General", icon: Note, tone: "bg-slate-100 text-slate-700" },
  { value: "avance", label: "Avance", icon: TrendUp, tone: "bg-emerald-50 text-emerald-700" },
  { value: "clima", label: "Clima", icon: Cloud, tone: "bg-blue-50 text-blue-700" },
  { value: "incidente", label: "Incidente", icon: Warning, tone: "bg-red-50 text-red-700" },
  { value: "decision", label: "Decisión", icon: Wrench, tone: "bg-orange-50 text-orange-700" },
];
const catMeta = (v) => CATS.find((c) => c.value === v) || CATS[0];
const catLabel = (v) => catMeta(v).label;

function printBitacora(entries, from, to) {
  const rows = entries.map((b) => `
    <div style="border-left:3px solid #FF5A00;padding:6px 12px;margin-bottom:12px">
      <div style="font-size:12px;color:#64748B">${b.entry_date} · ${catLabel(b.category)} · ${b.author} (${b.author_role})${b.contractor_name ? " · " + b.contractor_name : ""}</div>
      ${b.title ? `<div style="font-weight:bold">${b.title}</div>` : ""}
      <div style="white-space:pre-wrap;font-size:13px">${(b.description || "").replace(/</g, "&lt;")}</div>
      ${b.signature_path ? `<div style="font-size:11px;color:#16A34A;margin-top:2px">✔ Entrada firmada</div>` : ""}
    </div>`).join("");
  const html = `<html><head><title>Bitácora de obra</title><style>body{font-family:Arial,sans-serif;padding:30px;color:#0F172A}h1{color:#0F172A;margin:0}</style></head>
    <body><h1>Bitácora de obra</h1><p style="color:#64748B">Periodo: ${from || "inicio"} a ${to || "hoy"} · ${entries.length} entrada(s)</p>${rows}</body></html>`;
  const w = window.open("", "_blank");
  w.document.write(html); w.document.close();
  setTimeout(() => { try { w.print(); } catch (e) {} }, 400);
}

export default function Bitacora() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [canLog, setCanLog] = useState(false);
  const [filter, setFilter] = useState("");
  const [contractorView, setContractorView] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ entry_date: new Date().toISOString().slice(0, 10), category: "general", title: "", description: "", contractor_id: "", contract_id: "" });
  const [files, setFiles] = useState([]);
  const [camFiles, setCamFiles] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [tmUrl, setTmUrl] = useState("");
  const uploadCamToPhotos = async (file) => { const spec = (contractors.find((c) => c.id === form.contractor_id) || {}).name || ""; const fd = new FormData(); fd.append("specialty", spec); fd.append("files", file); try { await api.post("/photos/bulk", fd); } catch (e) { /* opcional */ } };
  const [signature, setSignature] = useState(null);
  const [coords, setCoords] = useState(null);

  const getLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocalización no disponible");
    navigator.geolocation.getCurrentPosition(
      (p) => { setCoords({ lat: p.coords.latitude.toFixed(6), lng: p.coords.longitude.toFixed(6) }); toast.success("Ubicación adjuntada"); },
      () => toast.error("No se pudo obtener la ubicación")
    );
  };

  const load = () => api.get("/bitacora").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/bitacora/can-log").then((r) => setCanLog(r.data.can_log));
    if (user?.role !== "contratista") api.get("/contractors").then((r) => setContractors(r.data));
  }, [user]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const fd = new FormData();
    fd.append("entry_date", form.entry_date); fd.append("category", form.category);
    fd.append("title", form.title); fd.append("description", form.description);
    fd.append("contractor_id", form.contractor_id); fd.append("contract_id", form.contract_id);
    [...files].forEach((f) => fd.append("files", f));
    camFiles.forEach((f) => fd.append("files", f));
    if (signature) fd.append("signature", signature);
    if (coords) { fd.append("lat", coords.lat); fd.append("lng", coords.lng); }
    try {
      await api.post("/bitacora", fd);
      toast.success("Entrada registrada en la bitácora"); setOpen(false);
      setForm({ entry_date: new Date().toISOString().slice(0, 10), category: "general", title: "", description: "", contractor_id: "", contract_id: "" });
      setFiles([]); setCamFiles([]); setSignature(null); setCoords(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => { if (!window.confirm("¿Eliminar entrada?")) return; await api.delete(`/bitacora/${id}`); load(); };

  const shown = items.filter((i) =>
    (!filter || i.category === filter) &&
    (!from || i.entry_date >= from) &&
    (!to || i.entry_date <= to) &&
    (contractorView === "all" ||
      (contractorView === "general" ? !i.contractor_id : i.contractor_id === contractorView)));

  return (
    <div>
      <PageHeader overline="Diario de obra" title="Bitácora"
        action={
          <div className="flex items-center gap-2">
            <button onClick={() => printBitacora(shown, from, to)} data-testid="export-bitacora-btn"
              className="inline-flex items-center gap-2 h-11 px-4 border border-input rounded-sm font-medium text-sm hover:bg-muted transition-colors duration-200">
              <Printer size={18} weight="bold" /> PDF
            </button>
            {canLog && <PrimaryButton onClick={() => setOpen(true)} data-testid="add-bitacora-btn"><Plus size={18} weight="bold" /> Nueva</PrimaryButton>}
          </div>
        } />

      <Card className="p-4 mb-6 text-sm text-muted-foreground border-l-4 border-l-primary">
        Registro cronológico de la obra: clima, incidentes, avances y decisiones, con <b className="text-foreground">firma</b> opcional. La alimenta cada <b className="text-foreground">contratista</b> y cualquier persona con <b className="text-foreground">autorización</b> (desde Usuarios). Las entradas quedan como historial y no se editan.
      </Card>

      {user?.role !== "contratista" && (
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <label className="text-sm font-semibold text-foreground">Bitácora de:</label>
          <select data-testid="bita-contractor-view" value={contractorView} onChange={(e) => setContractorView(e.target.value)}
            className="h-10 px-3 rounded-sm border border-input bg-white text-sm font-medium min-w-[240px]">
            <option value="all">Todas (empresa + contratistas)</option>
            <option value="general">General de la empresa</option>
            {contractors.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <span className="text-xs text-muted-foreground">{shown.length} entrada(s)</span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-6">
        <button onClick={() => setFilter("")} className={`px-3 py-1.5 rounded-sm text-sm font-medium border transition-colors duration-200 ${filter === "" ? "bg-primary text-white border-primary" : "bg-white border-input text-muted-foreground hover:bg-muted"}`}>Todas</button>
        {CATS.map((c) => (
          <button key={c.value} onClick={() => setFilter(c.value)} data-testid={`bita-filter-${c.value}`}
            className={`px-3 py-1.5 rounded-sm text-sm font-medium border transition-colors duration-200 ${filter === c.value ? "bg-primary text-white border-primary" : "bg-white border-input text-muted-foreground hover:bg-muted"}`}>{c.label}</button>
        ))}
        <div className="flex items-center gap-1 ml-auto text-sm">
          <input type="date" data-testid="bita-from" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 px-2 rounded-sm border border-input bg-white" />
          <span className="text-muted-foreground">a</span>
          <input type="date" data-testid="bita-to" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 px-2 rounded-sm border border-input bg-white" />
        </div>
      </div>

      {shown.length === 0 ? (
        <EmptyState icon={Notebook} title="Sin entradas" subtitle="Documenta el día a día de la obra."
          action={canLog && <PrimaryButton onClick={() => setOpen(true)}><Plus size={18} weight="bold" /> Nueva entrada</PrimaryButton>} />
      ) : (
        <div className="space-y-4">
          {shown.map((b) => {
            const m = catMeta(b.category);
            return (
              <Card key={b.id} data-testid={`bitacora-${b.id}`} className="p-5">
                <div className="flex items-start gap-4">
                  <div className="text-center flex-shrink-0 w-16">
                    <div className="font-heading font-black text-lg leading-none">{b.entry_date?.slice(8, 10)}</div>
                    <div className="text-xs text-muted-foreground uppercase">{b.entry_date?.slice(5, 7)}/{b.entry_date?.slice(2, 4)}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-semibold ${m.tone}`}><m.icon size={13} weight="bold" /> {m.label}</span>
                      {b.title && <span className="font-heading font-bold">{b.title}</span>}
                      {b.signature_path && <span className="inline-flex items-center gap-1 text-xs font-semibold text-success"><PenNib size={13} weight="fill" /> Firmada</span>}
                      {b.location && <a href={`https://maps.google.com/?q=${b.location.lat},${b.location.lng}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:underline"><MapPin size={13} weight="fill" /> Ubicación</a>}
                    </div>
                    <p className="text-sm mt-2 whitespace-pre-wrap">{b.description}</p>
                    {b.photos?.length > 0 && (
                      <div className="flex gap-2 mt-3 overflow-x-auto">
                        {b.photos.map((p, i) => <button key={i} type="button" data-testid={`bita-photo-${b.id}-${i}`} onClick={() => setLightbox({ url: fileUrl(p.path), name: b.title || "Foto de bitácora", minimized: false })}><img src={fileUrl(p.path)} alt="" className="w-20 h-20 rounded-sm object-cover border border-border hover:ring-2 hover:ring-primary transition-all" /></button>)}
                      </div>
                    )}
                    {b.signature_path && <img src={fileUrl(b.signature_path)} alt="firma" className="mt-3 h-16 border border-border rounded-sm bg-white" />}
                    <div className="text-xs text-muted-foreground mt-3">{b.author} ({b.author_role}){b.contractor_name ? ` · ${b.contractor_name}` : ""} · {new Date(b.created_at).toLocaleString("es-MX")}</div>
                  </div>
                  {(["admin", "gerente_general"].includes(user?.role) || user?.can_delete) && <button onClick={() => remove(b.id)} className="p-1.5 text-muted-foreground hover:text-danger"><Trash size={18} /></button>}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Nueva entrada de bitácora">
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Fecha"><input data-testid="bita-date" type="date" className={inputClass} value={form.entry_date} onChange={(e) => setForm({ ...form, entry_date: e.target.value })} /></Field>
            <Field label="Categoría">
              <select data-testid="bita-category" className={inputClass} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {CATS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Título"><input data-testid="bita-title" className={inputClass} placeholder="Ej. Suspensión por lluvia" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
          <Field label="Descripción *"><textarea data-testid="bita-description" required className={inputClass + " h-24 py-2"} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
          {user?.role !== "contratista" && (
            <Field label="Contratista (opcional)">
              <select className={inputClass} value={form.contractor_id} onChange={(e) => setForm({ ...form, contractor_id: e.target.value })}>
                <option value="">General</option>
                {contractors.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
          )}
          <Field label="Fotos (opcional)">
            <input data-testid="bita-files" type="file" accept="image/*" multiple onChange={(e) => setFiles(e.target.files)}
              className="w-full text-sm border border-dashed border-input rounded-sm p-3 file:mr-3 file:px-3 file:py-1.5 file:border-0 file:bg-primary file:text-white file:rounded-sm file:font-medium" />
          </Field>
          <Field label="Cámara con marca de tiempo (se marca fecha/hora/GPS y se guarda en Fotos de obra clasificada por contratista)">
            <TimemarkCamera author={user?.name} project={(contractors.find((c) => c.id === form.contractor_id) || {}).name} onCapture={(f) => { setCamFiles((p) => [...p, f]); uploadCamToPhotos(f); }} />
          </Field>
          {user?.role !== "contratista" && (
            <div className="text-xs">
              <button type="button" data-testid="timemark-connect-btn" onClick={async () => { try { const r = await api.get("/timemark/webhook-url"); setTmUrl(r.data.url); } catch (e) {} }} className="text-primary font-semibold hover:underline">Conectar la app Timemark (webhook)</button>
              {tmUrl && <div className="mt-1 space-y-1"><input readOnly data-testid="timemark-url" value={tmUrl} onFocus={(e) => e.target.select()} className={inputClass + " text-xs"} /><span className="text-muted-foreground block">Pégalo en Timemark → Integrations → Webhook (evento photo.uploaded). Las fotos entrarán a "Fotos de obra".</span></div>}
            </div>
          )}
          <Field label="Firma (opcional)"><SignaturePad onChange={setSignature} /></Field>
          <button type="button" onClick={getLocation} data-testid="bita-location-btn" className={`inline-flex items-center gap-2 h-10 px-4 rounded-sm font-medium text-sm border transition-colors duration-200 ${coords ? "border-success text-success bg-emerald-50" : "border-input hover:bg-muted"}`}>
            <MapPin size={18} weight="bold" /> {coords ? `Ubicación adjuntada (${coords.lat}, ${coords.lng})` : "Adjuntar mi ubicación"}
          </button>
          <PrimaryButton type="submit" disabled={busy} data-testid="save-bitacora-btn">{busy ? "Guardando…" : "Registrar entrada"}</PrimaryButton>
        </form>
      </Modal>
      <Lightbox item={lightbox} onClose={() => setLightbox(null)} onMinimize={(m) => setLightbox((v) => v && { ...v, minimized: m })} />
    </div>
  );
}
